/** @format */

import {
  APP_ID,
  ApplicationConfig,
  ErrorHandler,
  effect,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { Chain, Environment, LocalStorageConfig } from '@shared/data-access/interfaces';
import { CompareEffects, compareReducer } from '@plp/data-access/compare';
import {
  CustomUrlSerializer,
  KillswitchService,
  LoggerConfig,
  Store,
  TaggingService,
  WINDOW_PROVIDERS,
  StoreService,
  LoggerLevel,
} from '@shared/data-access/services';
import { Store as NgrxStore, provideState, provideStore } from '@ngrx/store';
import {
  PreloadAllModules,
  UrlSerializer,
  provideRouter,
  withEnabledBlockingInitialNavigation,
  withPreloading,
} from '@angular/router';
import { appInitializerFeatureFlagsSet, productListReducers } from '@plp/data-access/state';
import {
  commonReducer,
  headerLocationReducer,
  homrReducer,
  setUserStoreInfoFromCookie,
  setUserStoreName,
} from '@state/data-access';
import {
  provideClientHydration,
  withEventReplay,
  withNoIncrementalHydration,
} from '@angular/platform-browser';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';

import { APP_BASE_HREF } from '@angular/common';
import { AUTH_URL_FILTER } from '@shared/authentication/data-access';
import { CriteoService } from '@shared/data-access/product-recommendations';
import { DCSG_APP_NAME } from '@shared/data-access/constants';
import { GlobalErrorHandler } from '@logger';
import { HTTP_INTERCEPTOR_PROVIDERS } from './app.interceptors';
import { PRODUCT_LIST_APP_ROUTES } from './app.routes';
import { ProductAnalyticsService } from '@shared/data-access/analytics';
import { ProductListEffects } from '@plp/data-access/effects';
import { chain } from '@core/chain';
import { environment } from '../environments/environment';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';

const LOCAL_STORAGE_CONFIG: LocalStorageConfig = {
  prefix: '',
};
const LOGGER_CONFIG: LoggerConfig = {
  clientLoggerLevel: environment.logging?.client.level ?? LoggerLevel.LOG,
  xhrLoggerLevel: environment.logging?.xhr.level ?? LoggerLevel.LOG,
  xhrLoggerUrl: environment.api.spring.microServiceProxy.logger?.path
    ? `${environment.api.spring.microServiceProxy.logger.path}publish`
    : undefined,
};

/**
 * SRLP pages are still client-side rendered,
 * so we need to enable blocking navigation.
 */
const isSRLP = globalThis?.location?.pathname?.startsWith('/search/');

export const appConfig: ApplicationConfig = {
  providers: [
    Store,
    provideStore({
      common: commonReducer,
      homr: homrReducer,
      headerLocation: headerLocationReducer,
      router: routerReducer,
    }),
    provideEffects([ProductListEffects, CompareEffects]),
    provideState('productList', {
      productListCore: productListReducers,
    }),
    provideState('compare', compareReducer),
    provideRouter(
      PRODUCT_LIST_APP_ROUTES,
      withPreloading(PreloadAllModules), // add preloading for all routes
      ...(isSRLP ? [withEnabledBlockingInitialNavigation()] : []),
    ),
    provideRouterStore(),
    provideStoreDevtools({
      maxAge: environment.production ? 5 : 25,
      logOnly: environment.production,
      connectInZone: true,
    }),
    { provide: LoggerConfig, useValue: LOGGER_CONFIG },
    provideHttpClient(withInterceptorsFromDi()),
    { provide: Environment, useValue: environment },
    { provide: APP_ID, useValue: 'dcsg-ngx-plp-server' },
    { provide: APP_BASE_HREF, useValue: '/' },
    { provide: LocalStorageConfig, useValue: LOCAL_STORAGE_CONFIG },

    {
      provide: AUTH_URL_FILTER,
      useValue: [
        environment.baseUrls?.pointService,
        environment.baseUrls?.identityService,
        environment.baseUrls?.athleteService,
        environment.api.myLocker,
      ].filter(Boolean),
    },
    /*
     * APP_INITIALIZER InjectionToken is used to perform any logic during app initialization,
     * this would execute before AppComponent or any Resolve Guards.  This guarantees anything
     * required for setup is completed ahead of time.
     */
    provideAppInitializer(() => {
      const initializerFn = (
        (service: ProductAnalyticsService) => () =>
          service.resetAnalytics()
      )(inject(ProductAnalyticsService));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const killswitchService = inject(KillswitchService);
      const store = inject(NgrxStore);
      effect(() => {
        store.dispatch(
          appInitializerFeatureFlagsSet({
            featureFlags: killswitchService.killSwitchesForCurrentChain(),
          }),
        );
      });
    }),
    provideAppInitializer(() => {
      // Set initial value of store info before resolvers run
      const storeService = inject(StoreService);
      const ngrxStore = inject(NgrxStore);

      const storeCookieString = storeService.getSavedCustomerStore()?.trim();

      if (storeCookieString && !storeCookieString.includes('undefined')) {
        const dataFromStoreCookieString =
          storeService.getStoreDataFromStoreCookieString(storeCookieString);

        if (dataFromStoreCookieString?.storeZip || dataFromStoreCookieString?.storeNumber) {
          ngrxStore.dispatch(
            setUserStoreInfoFromCookie({
              zipcode: dataFromStoreCookieString.storeZip ?? '',
              location: dataFromStoreCookieString.storeNumber ?? '',
              name: dataFromStoreCookieString.storeName ?? '',
            }),
          );
          ngrxStore.dispatch(
            setUserStoreName({ storeName: dataFromStoreCookieString.storeName ?? '' }),
          );
        }
      }
    }),
    WINDOW_PROVIDERS,
    { provide: Chain, useValue: chain || {} },
    { provide: DCSG_APP_NAME, useValue: 'PLP' },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    ...HTTP_INTERCEPTOR_PROVIDERS,
    { provide: UrlSerializer, useClass: CustomUrlSerializer },
    TaggingService,
    provideClientHydration(withEventReplay(), withNoIncrementalHydration()),
    CriteoService,
    provideAnimationsAsync(),
  ],
};
